import { RouteDayRow, RouteDayStop } from "../../types/pivot.type";
import { fmtDistance, fmtMinuteOfDay, minutesFromDayStart } from "./routeTimeFormat";

/**
 * Segment kinds on the day timeline (board A2):
 * - `leave`    — branch checkout → first shop, dashed.
 * - `gap`      — previous shop's leave → this check-in, within the normal band.
 * - `gap_flag` — the same, over the bucket's p95 (a GAP tag).
 * - `lunch`    — the day's lunch gap.
 * - `app`      — check-in → first bill (the time spent in the app).
 * - `app_long` — the same, flagged APP.
 * - `back`     — last shop → count start, dashed.
 */
export type TimelineSegmentKind =
  | "leave"
  | "gap"
  | "gap_flag"
  | "lunch"
  | "app"
  | "app_long"
  | "back";

export interface TimelineSegment {
  kind: TimelineSegmentKind;
  /** `travel` holds the gaps and the dashed legs; `app` holds the app time. */
  track: "travel" | "app";
  /** Minutes from local midnight of the route-day. */
  start: number;
  end: number;
  tip: string;
}

export interface TimelineCallout {
  at: number;
  text: string;
}

export interface TimelineMarker {
  kind: "leave" | "count";
  at: number;
  label: string;
}

export interface Timeline {
  windowStart: number;
  windowEnd: number;
  segments: TimelineSegment[];
  callouts: TimelineCallout[];
  markers: TimelineMarker[];
}

/** 06:30 – 18:30, widened (to the half hour) only when the day runs outside it. */
export const TIMELINE_DEFAULT_START = 6 * 60 + 30;
export const TIMELINE_DEFAULT_END = 18 * 60 + 30;

/** When the van left a stop: first ORIGINAL print, else the bill, else the check-in. */
const leftAt = (s: RouteDayStop) => s.left_at ?? s.bill_at ?? s.check_in_at;

/**
 * Turn a route-day into positioned timeline pieces. Pure — the component only maps minutes
 * to percentages. A missing timestamp drops its piece rather than inventing one.
 */
export function buildTimeline(
  header: Pick<RouteDayRow, "date" | "leave_at" | "last_left_at" | "count_started_at">,
  stops: RouteDayStop[],
): Timeline {
  const at = (iso: string | null | undefined) => minutesFromDayStart(iso, header.date);
  const ordered = [...stops].sort((a, b) => a.seq - b.seq);
  const segments: TimelineSegment[] = [];
  const callouts: TimelineCallout[] = [];
  const markers: TimelineMarker[] = [];

  const leave = at(header.leave_at);
  const first = ordered.length ? at(ordered[0].check_in_at) : null;
  if (leave !== null) {
    markers.push({ kind: "leave", at: leave, label: `ออกสาขา ${fmtMinuteOfDay(leave)}` });
    if (first !== null && first > leave) {
      segments.push({
        kind: "leave",
        track: "travel",
        start: leave,
        end: first,
        tip: `ออกสาขา ${fmtMinuteOfDay(leave)} → ร้านแรก ${fmtMinuteOfDay(first)} · ${(
          first - leave
        ).toFixed(1)} นาที`,
      });
    }
  }

  ordered.forEach((stop, i) => {
    const checkIn = at(stop.check_in_at);
    if (checkIn === null) return;

    if (i > 0) {
      const from = at(leftAt(ordered[i - 1]));
      if (from !== null && checkIn > from) {
        const isLunch = stop.tags.includes("LUNCH");
        const isFlag = stop.tags.includes("GAP");
        const gap = stop.gap_min ?? checkIn - from;
        const excess =
          isFlag && stop.normal_min !== null ? Math.max(0, gap - stop.normal_min) : null;
        const parts = [
          `${fmtMinuteOfDay(from)}–${fmtMinuteOfDay(checkIn)}`,
          `${gap.toFixed(1)} นาที`,
        ];
        if (stop.distance_m !== null) parts.push(fmtDistance(stop.distance_m));
        if (isLunch) parts.push("พักเที่ยง");
        if (excess !== null) parts.push(`เกินปกติ +${excess.toFixed(1)} น.`);
        parts.push(`ไป ${stop.seq}. ${stop.customer_name}`);
        segments.push({
          kind: isFlag ? "gap_flag" : isLunch ? "lunch" : "gap",
          track: "travel",
          start: from,
          end: checkIn,
          tip: parts.join(" · "),
        });
        if (excess !== null)
          callouts.push({ at: (from + checkIn) / 2, text: `+${excess.toFixed(1)} น.` });
      }
    }

    const bill = at(stop.bill_at);
    if (bill !== null && bill >= checkIn) {
      const long = stop.tags.includes("APP");
      const appMin = stop.app_min ?? bill - checkIn;
      segments.push({
        kind: long ? "app_long" : "app",
        track: "app",
        start: checkIn,
        end: bill,
        tip: `${stop.seq}. ${stop.customer_name} · เช็คอิน ${fmtMinuteOfDay(
          checkIn,
        )} · ทำรายการ ${appMin.toFixed(1)} นาที`,
      });
      if (long) callouts.push({ at: checkIn, text: `แอป ${appMin.toFixed(1)} น.` });
    }
  });

  const count = at(header.count_started_at);
  const last = ordered.length
    ? at(leftAt(ordered[ordered.length - 1]))
    : at(header.last_left_at);
  if (count !== null) {
    markers.push({ kind: "count", at: count, label: `เริ่มตรวจนับ ${fmtMinuteOfDay(count)}` });
    if (last !== null && count > last) {
      segments.push({
        kind: "back",
        track: "travel",
        start: last,
        end: count,
        tip: `ร้านสุดท้าย ${fmtMinuteOfDay(last)} → เริ่มตรวจนับ ${fmtMinuteOfDay(count)} · ${(
          count - last
        ).toFixed(1)} นาที`,
      });
    }
  }

  const points = [...segments.flatMap((s) => [s.start, s.end]), ...markers.map((m) => m.at)];
  const lo = points.length ? Math.min(...points) : TIMELINE_DEFAULT_START;
  const hi = points.length ? Math.max(...points) : TIMELINE_DEFAULT_END;

  return {
    windowStart: Math.min(TIMELINE_DEFAULT_START, Math.floor(lo / 30) * 30),
    windowEnd: Math.max(TIMELINE_DEFAULT_END, Math.ceil(hi / 30) * 30),
    segments,
    callouts,
    markers,
  };
}
